import {
  DevHubCheckbox,
  DevHubIconButton,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import { Application } from '../types';
import { getDataLeadFullName, normalizeLogin } from '../utils/dataLeadUtils';
import { ASSIGNMENT_MODE_COLORS } from '../constants/colors';
import {
  ASSIGNMENT_MODES,
  ASSIGNMENT_MODE_LABELS,
  ASSIGNMENT_MODE_TOOLTIPS,
  type AssignmentMode,
} from '../constants/assignmentModes';

interface TemplateProps {
  activeFunctionalAreaApps: Application[];
  dataLeadAssignments: Map<number, Set<string>>;
  selectedAppsForUpdate: Set<number>;
  functionalAreasData: any;
  selectedFunctionalArea: string;
  applicationModes?: Map<number, AssignmentMode>;
  onToggleAssignment: (
    applicationId: number,
    leadLogin: string,
    isAssigned: boolean,
  ) => void;
  onResetRow: (applicationId: number) => void;
  hasRowChanges: (applicationId: number) => boolean;
}

/**
 * Create checkbox template for data lead assignments
 * @param dataLeadAssignments - Map of application IDs to their assigned data leads
 */
export const createCheckboxTemplate =
  ({
    dataLeadAssignments,
    selectedAppsForUpdate,
    onToggleAssignment,
    applicationModes,
    functionalAreasData,
    selectedFunctionalArea,
  }: TemplateProps) =>
  (leadLogin: string) =>
  (rowData: any) => {
    const appAssignments =
      dataLeadAssignments.get(rowData.applicationId) || new Set();
    // Case-insensitive check for data lead assignment
    const isChecked = Array.from(appAssignments).some(
      assignedLogin =>
        normalizeLogin(assignedLogin) === normalizeLogin(leadLogin),
    );
    const isRowSelected = selectedAppsForUpdate.has(rowData.applicationId);
    const mode =
      applicationModes?.get(rowData.applicationId) || ASSIGNMENT_MODES.DEFAULT;
    const isDefaultMode = mode === ASSIGNMENT_MODES.DEFAULT;

    // Get the lead's full name for the tooltip
    const leadFullName = getDataLeadFullName(
      leadLogin,
      functionalAreasData,
      selectedFunctionalArea,
    );

    // Create tooltip text based on current state and selection - matching select all pattern
    let tooltipText: string;
    if (!isRowSelected) {
      tooltipText = `Select application to modify ${leadFullName} assignment`;
    } else if (isChecked) {
      tooltipText = `Remove ${leadFullName} from ${rowData.name}`;
    } else {
      tooltipText = `Add ${leadFullName} to ${rowData.name}`;
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          opacity: isDefaultMode ? 0.6 : 1, // Reduced opacity for Default mode
        }}
      >
        <DevHubTooltip title={tooltipText}>
          <DevHubCheckbox
            checked={isChecked}
            disabled={!isRowSelected}
            onChange={event => {
              onToggleAssignment(
                rowData.applicationId,
                leadLogin,
                event.target.checked,
              );
            }}
            size="small"
            color="primary"
            inputProps={{
              'aria-label': `Assign ${getDataLeadFullName(leadLogin, functionalAreasData, selectedFunctionalArea)} to ${rowData.name || rowData.applicationId}`,
            }}
          />
        </DevHubTooltip>
      </div>
    );
  };

/**
 * Create reset template for individual rows
 * @param props - Template properties
 * @param props.selectedAppsForUpdate - Set of selected application IDs for update
 * @param props.hasRowChanges - Function to check if a row has changes
 * @param props.onResetRow - Function to reset a row to original state
 */
export const createResetTemplate =
  ({ selectedAppsForUpdate, hasRowChanges, onResetRow }: TemplateProps) =>
  () =>
  (rowData: any) => {
    const hasChanges = hasRowChanges(rowData.applicationId);
    const isRowSelected = selectedAppsForUpdate.has(rowData.applicationId);

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <DevHubTooltip title="Reset row to original state">
          <span>
            <DevHubIconButton
              size="small"
              disabled={!hasChanges || !isRowSelected}
              onClick={() => onResetRow(rowData.applicationId)}
            >
              <SettingsBackupRestoreIcon fontSize="small" />
            </DevHubIconButton>
          </span>
        </DevHubTooltip>
      </div>
    );
  };

/**
 * Create application name template
 * @param activeFunctionalAreaApps - Array of active functional area applications
 */
export const createApplicationNameTemplate =
  ({ activeFunctionalAreaApps }: TemplateProps) =>
  () =>
  (rowData: any) => {
    const app = activeFunctionalAreaApps.find(
      a => a.applicationId === rowData.applicationId,
    );
    if (!app) return rowData.name;

    return app.name;
  };

/**
 * Create select all filter template for data lead columns
 * @param props - Template properties
 * @param props.dataLeadAssignments - Map of application IDs to their assigned data leads
 * @param props.selectedAppsForUpdate - Set of application IDs selected for update
 * @param props.functionalAreasData - Functional areas data
 * @param props.selectedFunctionalArea - Currently selected functional area
 * @param props.onToggleAssignment - Function to toggle data lead assignment
 */
export const createSelectAllFilterTemplate =
  ({
    dataLeadAssignments,
    selectedAppsForUpdate,
    functionalAreasData,
    selectedFunctionalArea,
    onToggleAssignment,
  }: TemplateProps) =>
  (leadLogin: string, rows: any[]) =>
  () => {
    const visibleRows = rows; // This would be filtered rows in a real implementation
    const allSelected = visibleRows.every(row => {
      const appAssignments =
        dataLeadAssignments.get(row.applicationId) || new Set();
      // Case-insensitive check for select all
      return Array.from(appAssignments).some(
        assignedLogin =>
          normalizeLogin(assignedLogin) === normalizeLogin(leadLogin),
      );
    });
    const someSelected = visibleRows.some(row => {
      const appAssignments =
        dataLeadAssignments.get(row.applicationId) || new Set();
      // Case-insensitive check for select all
      return Array.from(appAssignments).some(
        assignedLogin =>
          normalizeLogin(assignedLogin) === normalizeLogin(leadLogin),
      );
    });

    // Find the lead's full name
    const leadFullName = getDataLeadFullName(
      leadLogin,
      functionalAreasData,
      selectedFunctionalArea,
    );

    // Conditional tooltip based on current state
    const tooltipText = allSelected
      ? `Remove ${leadFullName} from all Applications`
      : `Add ${leadFullName} to all Applications`;

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          padding: '4px',
        }}
      >
        <DevHubTooltip title={tooltipText}>
          <DevHubCheckbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={event => {
              const newValue = event.target.checked;
              visibleRows.forEach(row => {
                const isRowSelected = selectedAppsForUpdate.has(
                  row.applicationId,
                );
                if (isRowSelected) {
                  onToggleAssignment(row.applicationId, leadLogin, newValue);
                }
              });
            }}
            size="small"
            color="primary"
            inputProps={{
              'aria-label': `Toggle all assignments for ${getDataLeadFullName(leadLogin, functionalAreasData, selectedFunctionalArea)}`,
            }}
          />
        </DevHubTooltip>
      </div>
    );
  };

/**
 * Creates a mode template function that displays assignment mode chips
 * @param applicationModes - Map of application IDs to their assignment mode
 */
export const createModeTemplate =
  (applicationModes: Map<number, AssignmentMode>) => (row: Application) => {
    const mode =
      applicationModes.get(row.applicationId) || ASSIGNMENT_MODES.DEFAULT;
    const isDefault = mode === ASSIGNMENT_MODES.DEFAULT;

    const tooltipText = ASSIGNMENT_MODE_TOOLTIPS[mode];

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}
        className="mode-chip-container"
      >
        <DevHubTooltip title={tooltipText}>
          <div
            style={{
              backgroundColor: isDefault
                ? ASSIGNMENT_MODE_COLORS.default.background
                : ASSIGNMENT_MODE_COLORS.custom.background,
              color: isDefault
                ? ASSIGNMENT_MODE_COLORS.default.text
                : ASSIGNMENT_MODE_COLORS.custom.text,
              border: `1px solid ${isDefault ? ASSIGNMENT_MODE_COLORS.default.border : ASSIGNMENT_MODE_COLORS.custom.border}`,
              fontWeight: 500,
              minWidth: '60px',
              borderRadius: '8px',
              padding: '4px 8px',
              fontSize: '0.75rem',
              lineHeight: '1.43',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '24px',
            }}
          >
            {ASSIGNMENT_MODE_LABELS[mode]}
          </div>
        </DevHubTooltip>
      </div>
    );
  };
